/**
 * Single API for shoebox floor-plan geometry used by public tools.
 * Coordinates: x from left wall, y from front wall (meters).
 */

export interface RoomSize {
  lengthM: number;
  widthM: number;
}

export interface Point2 {
  /** From left wall (m). */
  xM: number;
  /** From front wall (m). */
  yM: number;
}

export interface RoomBounds {
  length: { min: number; max: number };
  width: { min: number; max: number };
}

/** Default limits shared by tool floor plans. */
export const DEFAULT_ROOM_BOUNDS: RoomBounds = {
  length: { min: 2.5, max: 14 },
  width: { min: 2.2, max: 12 },
};

export function roundM(n: number, digits = 2): number {
  const f = 10 ** digits;
  return Math.round(n * f) / f;
}

export function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

export function clampRoomSize(
  lengthM: number,
  widthM: number,
  bounds: RoomBounds = DEFAULT_ROOM_BOUNDS,
): RoomSize {
  return {
    lengthM: roundM(clamp(lengthM, bounds.length.min, bounds.length.max)),
    widthM: roundM(clamp(widthM, bounds.width.min, bounds.width.max)),
  };
}

/** Keep a point inside the room with a margin from walls. */
export function clampPointInRoom(
  point: Point2,
  room: RoomSize,
  marginM = 0.15,
): Point2 {
  const maxX = Math.max(marginM, room.widthM - marginM);
  const maxY = Math.max(marginM, room.lengthM - marginM);
  return {
    xM: roundM(clamp(point.xM, marginM, maxX)),
    yM: roundM(clamp(point.yM, marginM, maxY)),
  };
}

/**
 * Apply a room resize and pull every content point back inside.
 * Use this for wall-handle drags, slider edits, and typed values.
 */
export function resizeRoom(
  current: RoomSize,
  next: Partial<RoomSize>,
  contents: Record<string, Point2>,
  opts?: { marginM?: number; bounds?: RoomBounds },
): { room: RoomSize; contents: Record<string, Point2> } {
  const bounds = opts?.bounds ?? DEFAULT_ROOM_BOUNDS;
  const marginM = opts?.marginM ?? 0.15;
  const room = clampRoomSize(
    next.lengthM ?? current.lengthM,
    next.widthM ?? current.widthM,
    bounds,
  );
  const clamped: Record<string, Point2> = {};
  for (const [id, p] of Object.entries(contents)) {
    clamped[id] = clampPointInRoom(p, room, marginM);
  }
  return { room, contents: clamped };
}

/**
 * Map a pointer position on the SVG into room meters.
 *
 * When `clampToRoom` is set, the point is limited to the current footprint
 * (marker drag). When null/undefined, there is no upper clamp to the current
 * size — required so wall handles can grow past the current edge (CAD-style).
 */
export function pointerToRoomMeters(args: {
  clientX: number;
  clientY: number;
  svgRect: DOMRect;
  svgW: number;
  svgH: number;
  pad: number;
  scale: number;
  /** If provided, clamp into this room; if omitted, only floor at 0. */
  clampToRoom?: RoomSize | null;
}): Point2 {
  const { clientX, clientY, svgRect, svgW, svgH, pad, scale, clampToRoom } = args;
  if (svgRect.width <= 0 || svgRect.height <= 0 || scale <= 0) {
    return { xM: 0, yM: 0 };
  }
  const xSvg = ((clientX - svgRect.left) / svgRect.width) * svgW;
  const ySvg = ((clientY - svgRect.top) / svgRect.height) * svgH;
  const xM = (xSvg - pad) / scale;
  const yM = (ySvg - pad) / scale;

  if (clampToRoom) {
    return clampPointInRoom({ xM, yM }, clampToRoom, 0);
  }
  // Resize path: allow beyond current size; still non-negative.
  return { xM: Math.max(0, xM), yM: Math.max(0, yM) };
}

/** Resolve a typed display value (m or ft) into clamped meters for a dim axis. */
export function parseDimensionMeters(
  raw: string,
  unit: 'm' | 'ft',
  axis: 'length' | 'width' | 'free',
  opts?: { minM?: number; maxM?: number; bounds?: RoomBounds },
): number | null {
  const n = parseFloat(raw.replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  const meters = unit === 'ft' ? n / 3.28084 : n;
  if (axis === 'length') {
    const b = opts?.bounds ?? DEFAULT_ROOM_BOUNDS;
    return roundM(clamp(meters, opts?.minM ?? b.length.min, opts?.maxM ?? b.length.max));
  }
  if (axis === 'width') {
    const b = opts?.bounds ?? DEFAULT_ROOM_BOUNDS;
    return roundM(clamp(meters, opts?.minM ?? b.width.min, opts?.maxM ?? b.width.max));
  }
  const min = opts?.minM ?? 0.05;
  const max = opts?.maxM ?? 100;
  return roundM(clamp(meters, min, max));
}
